import { NgTemplateOutlet } from '@angular/common';
import {
  AfterContentChecked,
  Component,
  effect,
  ElementRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { RuleId } from '../../models/rule-id';
import { ExplainerService } from '../../services/explainer.service';
import { FilterNavigationBase } from '../filter-navigation-base';

@Component({
  selector: '[filter-title]',
  template: `
<span class="filter-title-text">{{ text }}</span>
<div class="filter-tip" [class.pinned]="pinned" [class.filter-tip-empty]="!ruleExplainerHtml && !hasProjectedContent" (click)="pinned = !pinned">
  <p (click)="$event.stopPropagation()">
    <span #projectedContent class="filter-tip-projected" [class.hidden]="!hasProjectedContent">
      <ng-content></ng-content>
    </span>
    @if (ruleExplainerHtml) {
      <span class="rule-explainer-hint" [class.tooltip-section]="hasProjectedContent">
        <span class="icon icon-tutor"></span>
        <span [innerHTML]="ruleExplainerHtml"></span>
        @if (pinned) {
          <a class="remove-warning-link" (click)="stopRuleExplainers($event)">(Stop showing rule explainers)</a>
        }
      </span>
    }
  </p>
</div>
<ng-template [ngTemplateOutlet]="additionalContent"></ng-template>
@if (activeRules !== undefined) {
  <span class="active-filters">{{ activeRules }} active rule{{ activeRules === 1 ? '' : 's' }}</span>
}
  `,
  styles: `
.filter-tip::before {
  pointer-events: none;
}

.filter-tip {
  cursor: pointer;
}

.filter-tip.filter-tip-empty {
  display: none;
}

.filter-tip.pinned {
  p {
    display: block !important;
    cursor: default;
  }
}

.filter-tip.pinned::before {
  content: "(x)";
}

.rule-explainer-hint {
  display: block;

  .icon {
    opacity: 0.55;
    margin-right: 3px;
    vertical-align: baseline;
  }
}

.filter-tip-projected.hidden {
  display: none;
}

.tooltip-section {
  display: block;
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px solid #465260;
}

.active-filters {
  float: right;
  color: #afa89fbd !important;
}

.remove-warning-link {
  opacity: 0.3;
  font-weight: normal;
}

@media (hover: none) and (pointer: coarse) {
  ::ng-deep .filter-title .filter-tip:hover p {
    display: none !important;
  }
}
  `,
  host: { 'class': 'filter-title' },
  imports: [NgTemplateOutlet]
})
export class FilterTitleComponent extends FilterNavigationBase implements OnChanges, AfterContentChecked {
  @Input() text: string = '';
  @Input() additionalContent: TemplateRef<any> | null = null;
  @Input() activeRules?: number;
  @Input() declare ruleId?: RuleId;
  @Input() declare ruleIds?: RuleId[];
  @Input() declare prefixMatch?: boolean;
  @Input() hideRuleExplainers = false;
  @ViewChild('projectedContent') projectedContent?: ElementRef<HTMLElement>;

  pinned: boolean = false;
  ruleExplainerHtml: SafeHtml | null = null;
  hasProjectedContent = false;

  private readonly explainerService = inject(ExplainerService);

  constructor() {
    super();
    effect(() => {
      this.settings.showPasteRuleExplainers();
      this.explainerService.loaded();
      this.updateRuleExplainerHtml();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ruleId'] || changes['ruleIds'] || changes['hideRuleExplainers']) {
      this.updateRuleExplainerHtml();
    }
  }

  ngAfterContentChecked(): void {
    const text = this.projectedContent?.nativeElement.textContent?.trim() ?? '';
    const hasContent = text.length > 0;
    if (hasContent !== this.hasProjectedContent) {
      this.hasProjectedContent = hasContent;
    }
  }

  stopRuleExplainers(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.settings.dismissPasteRuleExplainers();
  }

  private updateRuleExplainerHtml(): void {
    const html = this.hideRuleExplainers || !this.settings.showPasteRuleExplainers() || !this.explainerService.loaded() || !this.hasRuleId
      ? null
      : this.explainerService.getCombinedRuleExplainerHtml(this.effectiveRuleIds);

    if (html !== this.ruleExplainerHtml) {
      this.ruleExplainerHtml = html;
    }
  }
}
